import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Loader2, CalendarClock, AlertTriangle, ArrowUpRight, Flag } from "lucide-react";
import api from "@/lib/api";

export const Mono = ({ children, className = "", ...rest }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`} {...rest}>{children}</span>
);

export const CAT_STYLES = {
  EW: { label: "Emergency Works", chip: "bg-amber-400/15 text-amber-300 border-amber-400/40", dot: "bg-amber-400" },
  IRW: { label: "Immediate Reconstruction", chip: "bg-sky-400/15 text-sky-300 border-sky-400/40", dot: "bg-sky-400" },
  EPAR: { label: "EPAR", chip: "bg-gold-500/15 text-gold-300 border-gold-500/40", dot: "bg-gold-400" },
  CLAIMS: { label: "Claim Lodgement", chip: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40", dot: "bg-emerald-400" },
  COMPLIANCE: { label: "Compliance", chip: "bg-red-400/15 text-red-300 border-red-400/40", dot: "bg-red-400" },
};

export const STATUS_STYLES = {
  done: { label: "Complete", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40" },
  overdue: { label: "Overdue", cls: "bg-red-500/20 text-red-300 border-red-500/50" },
  due_soon: { label: "Due soon", cls: "bg-amber-400/15 text-amber-300 border-amber-400/50" },
  upcoming: { label: "Upcoming", cls: "bg-white/5 text-white/50 border-white/15" },
};

export const fmtDate = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

export const Countdown = ({ days, done }) => {
  if (done) return <span className="font-mono text-xs text-emerald-300">done</span>;
  if (days < 0) return <span className="font-mono text-xs text-red-300 font-bold">{Math.abs(days)}d over</span>;
  if (days <= 30) return <span className="font-mono text-xs text-amber-300 font-bold">{days}d left</span>;
  return <span className="font-mono text-xs text-white/45">{days}d left</span>;
};

export default function DrfaPrograms() {
  const [programs, setPrograms] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/fieldclaim/programs").then((r) => setPrograms(r.data)).catch(() => setPrograms([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8 text-white" data-testid="drfa-programs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">DRFA program tracker · deadlines per NSWRA</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">DRFA Programs</h1>
          <p className="text-white/55 mt-3 font-light max-w-2xl">
            Enter an event (AGRN + event date) and the full NSWRA deadline program is pushed out automatically —
            emergency works, IRW, the 30-September claim rule, EPAR application, ERC and the 24-month allowable time limit.
          </p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-program-btn" className="bg-gold-500 text-navy-950 font-semibold px-6 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2">
          <Plus className="h-4 w-4" /> New program
        </button>
      </div>

      {programs === null ? (
        <div className="text-white/40 text-sm">Loading programs…</div>
      ) : programs.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 p-16 text-center text-white/45 font-light" data-testid="programs-empty">
          No DRFA programs yet. Create one from an AGRN activation — the whole deadline program is generated from the event date.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {programs.map((p) => <ProgramCard key={p.id} p={p} onOpen={() => nav(`/dashboard/drfa/${p.id}`)} />)}
        </div>
      )}

      {open && <ProgramModal onClose={() => setOpen(false)} onSaved={(p) => { setOpen(false); nav(`/dashboard/drfa/${p.id}`); }} />}
    </div>
  );
}

function ProgramCard({ p, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`program-card-${p.id}`}
      className="group text-left border border-gold-500/25 bg-navy-900/40 p-6 hover:bg-navy-800 transition-colors relative overflow-hidden">
      <div className="absolute top-0 left-0 h-0.5 w-full bg-navy-800">
        <div className="h-full bg-gold-500 transition-all" style={{ width: `${p.elapsed_pct}%` }} />
      </div>
      <div className="flex items-center justify-between mb-4">
        <span className="bg-orb-metallic text-navy-950 font-mono font-bold text-[0.65rem] tracking-widest px-2.5 py-1 rounded-sm" data-testid={`program-agrn-${p.id}`}>
          {p.agrn || "NO AGRN"}
        </span>
        <ArrowUpRight className="h-4 w-4 text-white/25 group-hover:text-gold-400 transition-colors" />
      </div>
      <h3 className="font-heading text-lg leading-snug">{p.name}</h3>
      <div className="flex items-center gap-3 mt-2 text-xs text-white/45 font-light">
        <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3 text-gold-500/60" /> {fmtDate(p.event_date)}</span>
        <span className="font-mono">{p.days_since_event}d since event</span>
      </div>
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <Mono className="text-[0.5rem] text-white/40">Milestones</Mono>
          <span className="font-mono text-xs text-gold-300">{p.progress.done}/{p.progress.total}</span>
        </div>
        <div className="h-1 bg-navy-800 overflow-hidden">
          <div className="h-full bg-gold-500" style={{ width: `${p.progress.pct}%` }} />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4 flex-wrap">
        {p.overdue_count > 0 && (
          <span className="inline-flex items-center gap-1 border border-red-500/50 bg-red-500/15 text-red-300 text-[0.6rem] font-mono px-2 py-0.5 rounded-sm" data-testid={`program-overdue-${p.id}`}>
            <AlertTriangle className="h-3 w-3" /> {p.overdue_count} overdue
          </span>
        )}
        {p.next_due && (
          <span className="inline-flex items-center gap-1 border border-gold-500/30 text-gold-300/90 text-[0.6rem] font-mono px-2 py-0.5 rounded-sm">
            <Flag className="h-3 w-3" /> next: {p.next_due.title.length > 26 ? p.next_due.title.slice(0, 26) + "…" : p.next_due.title} · {p.next_due.days_left}d
          </span>
        )}
      </div>
    </button>
  );
}

const inp = "w-full bg-navy-950 border border-gold-500/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500";
const lbl = "font-mono uppercase tracking-[0.2em] text-[0.55rem] text-white/45 block mb-1.5";

export function ProgramModal({ program, onClose, onSaved }) {
  const [agrns, setAgrns] = useState([]);
  const [claims, setClaims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [agrnFocus, setAgrnFocus] = useState(false);
  const [form, setForm] = useState({
    name: program?.name || "", agrn: program?.agrn || "", event_date: program?.event_date || "",
    access_date: program?.access_date || "", day_labour_opt_in: program?.day_labour_opt_in ?? true,
    claim_id: program?.claim_id || "", council: program?.council || "",
  });

  useEffect(() => {
    api.get("/fieldclaim/agrn").then((r) => setAgrns(r.data)).catch(() => {});
    api.get("/fieldclaim/claims").then((r) => setClaims(r.data)).catch(() => {});
  }, []);

  const pickAgrn = (act) => {
    setForm((f) => ({ ...f, agrn: act.agrn, name: act.name, event_date: act.start_date }));
    setAgrnFocus(false);
  };

  const digits = (s) => (s || "").replace(/\D/g, "");
  const agrnMatches = form.agrn
    ? agrns.filter((a) => (digits(form.agrn) ? a.agrn.includes(digits(form.agrn)) : a.agrn.toLowerCase().includes(form.agrn.toLowerCase())))
    : agrns;
  const showAgrnList = agrnFocus && agrnMatches.length > 0 && !agrnMatches.some((a) => a.agrn === form.agrn && form.event_date);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    const payload = { ...form, claim_id: form.claim_id || null, access_date: form.access_date || null, name: form.name || null, council: form.council || null };
    try {
      const { data } = program
        ? await api.patch(`/fieldclaim/programs/${program.id}`, payload)
        : await api.post("/fieldclaim/programs", payload);
      onSaved(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not save the program.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="program-modal"
        className="bg-navy-900 border border-gold-500/30 w-full max-w-lg p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl">{program ? "Edit program" : "New DRFA program"}</h2>
          <button type="button" onClick={onClose} data-testid="program-modal-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <div className="relative">
          <label className={lbl}>AGRN activation (auto-fills name + event date)</label>
          <input value={form.agrn} autoComplete="off"
            onChange={(e) => setForm((f) => ({ ...f, agrn: e.target.value }))}
            onFocus={() => setAgrnFocus(true)}
            onBlur={() => setTimeout(() => setAgrnFocus(false), 180)}
            placeholder="1198 or AGRN 1198" className={inp} data-testid="program-agrn-input" />
          {showAgrnList && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-navy-950 border border-gold-500/30 z-20 max-h-56 overflow-y-auto shadow-xl" data-testid="agrn-suggestions">
              {agrnMatches.map((a) => (
                <button key={a.agrn} type="button" onMouseDown={() => pickAgrn(a)}
                  data-testid={`agrn-opt-${a.agrn.replace(/\D/g, "")}`}
                  className="w-full text-left px-3 py-2.5 hover:bg-gold-500/10 transition-colors">
                  <div className="font-mono text-xs text-gold-300">{a.agrn}</div>
                  <div className="text-white/55 text-xs font-light">{a.name} · {a.start_date}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className={lbl}>Program name</label>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inp} data-testid="program-name-input" placeholder="NSW Severe Weather and Flooding" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Event date *</label>
            <input type="date" required value={form.event_date} onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))} className={inp} data-testid="program-event-date" />
          </div>
          <div>
            <label className={lbl}>Asset access date</label>
            <input type="date" value={form.access_date} onChange={(e) => setForm((f) => ({ ...f, access_date: e.target.value }))} className={inp} data-testid="program-access-date" />
          </div>
        </div>
        <label className="flex items-center gap-3 border border-gold-500/20 bg-navy-950 px-3 py-3 cursor-pointer" data-testid="program-daylabour-toggle">
          <input type="checkbox" checked={form.day_labour_opt_in} onChange={(e) => setForm((f) => ({ ...f, day_labour_opt_in: e.target.checked }))} className="accent-gold-500" />
          <span className="text-sm text-white/75">Opted in to NSW Day Labour Co-Funding <span className="text-white/40">(EW window 3 months; otherwise 21 days)</span></span>
        </label>
        <div>
          <label className={lbl}>Link to a FieldClaim event (optional)</label>
          <select value={form.claim_id} onChange={(e) => setForm((f) => ({ ...f, claim_id: e.target.value }))} className={inp} data-testid="program-claim-select">
            <option value="">— none —</option>
            {claims.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {err && <div className="text-red-300 text-sm" data-testid="program-modal-error">{String(err)}</div>}
        <button type="submit" disabled={busy || !form.event_date} data-testid="program-modal-save"
          className="w-full bg-gold-500 text-navy-950 font-semibold py-3 hover:bg-gold-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
          {program ? "Recompute program" : "Generate deadline program"}
        </button>
      </form>
    </div>
  );
}
